/*
 * Confidential and Proprietary.
 * Do not distribute without 1-800-Flowers.com, Inc. consent.
 * Copyright 1-800-Flowers.com, Inc. 2019. All rights reserved.
 */

import React from 'react';
import {
    object, string, shape, arrayOf, number, bool,
} from 'prop-types';
import { format } from 'date-fns';
import { compose } from 'recompose';
import withStyles from '@material-ui/core/styles/withStyles';
import ReactHtmlParser from 'react-html-parser';

const styles = (theme) => ({
    crossedOutRetailPrice: {
        textDecoration: 'line-through',
        color: theme.palette.contentColor,
        fontWeight: '400',
        position: 'absolute',
        top: '0px',
        bottom: '0px',
        right: '72px',
        fontSize: '18px',
        [theme.breakpoints.down('sm')]: {
            right: '50px',
            fontSize: '.8em',
        },
        [theme.breakpoints.down('xs')]: {
            position: 'unset',
            fontSize: '1em',
            fontWeight: '600',
        },
    },

    salePrice: {
        color: theme.palette.primaryButton,
        margin: '0 0 0 10px',
        fontSize: '20px',
        position: 'absolute',
        right: '0px',
        top: '0px',
    },
    selectedSalePrice: {
        position: 'absolute',
        right: '0px',
        top: '0px',
        fontWeight: 'bold',
        fontSize: '20px',
        [theme.breakpoints.down('sm')]: {
            fontSize: '.9em',
        },
        [theme.breakpoints.down('xs')]: {
            position: 'unset',
            display: 'block',
            fontSize: '1em',
            fontWeight: '700',
        },
    },
    discountAmount: {
        color: theme.palette.cms?.skuPriceDiscountColor,
        fontWeight: 'normal',
        marginLeft: '10px',
        position: 'absolute',
        top: '26px',
        right: '0px',
        fontSize: '18px',
        [theme.breakpoints.down('sm')]: {
            fontSize: '.9em',
        },
        [theme.breakpoints.down('xs')]: {
            display: 'none',
        },
    },

    retailPrice: {
        fontSize: '20px',
        color: theme.palette.common?.black,
    },
    selectedRetailPrice: {
        fontWeight: 'bold',
        color: theme.palette.common?.black,
        fontSize: '20px',
    },
    productAvailibilityMessage: {
        textTransform: 'uppercase',
        fontSize: '11px',
        fontFamily: theme.typography.fontFamily,
        fontWeight: '700',
        color: theme.palette.productAvailibilityPDP,
        margin: '-2px 0px 0px 0px',
    },
    availabletoShip: {
        textTransform: 'uppercase',
        fontSize: '11px',
        fontFamily: theme.typography.fontFamily,
        fontWeight: '700',
        margin: '-2px 0px 0px 0px',
    },
    productHeaderWrapper: {
        display: 'block',
        padding: 0,
        // Mobile
        [theme.breakpoints.up(600)]: {
            display: 'none',
        },
    },
    titleAndPrice: {
        display: 'flex',
    },
    title: {
        flex: '4 0 0',
        color: theme.palette.black,
        margin: '0',
        fontSize: '1.25em',
        fontWeight: '500',
    },
    price: {
        flex: '1 0 0',
        textAlign: 'right',
        position: 'relative',
        fontSize: '1em',
        fontWeight: '600',
    },
    productId: {
        margin: '12px 0',
        fontSize: '1em',
        color: theme.palette.colorSecondary,
    },
    availability: {
        color: theme.palette.black,
        padding: '5px 0 10px',
        fontSize: '0.9em',
        boxSizing: 'border-box',
        textAlign: 'center',
        fontWeight: '700',
        textTransform: 'capitalize',
    },
});

const ProductNameRender = ({
    classes,
    selectedSku,
    product,
    isPassportBundleProduct,
}) => {
    const renderSkuPrice = (sku, clubPrice = false) => {
        const round = (value) => Number(`${Math.round(value * 10) / 10}`).toFixed(2);
        const priceNonSelected = clubPrice ? classes.clubPrice : classes.retailPrice;
        const priceSelected = clubPrice ? classes.clubPriceSelected : classes.selectedRetailPrice;
        if (sku?.prices?.[0] && sku?.prices?.[1]) {
            let productPrice = sku.prices[0].value;
            let discountedProductPrice = sku.prices[1].value;
            if (isPassportBundleProduct?.enable) {
                productPrice = (sku.prices[0].value + isPassportBundleProduct?.prices?.value).toFixed(2);
                discountedProductPrice = (sku.prices[1].value + isPassportBundleProduct?.prices?.value).toFixed(2);
            }
            return sku.prices[0].value > sku.prices[1].value
                ? (
                    <>
                        <span className={classes.crossedOutRetailPrice}>
                            ${productPrice}
                        </span>
                        <span className={selectedSku.id !== sku.id ? classes.salePrice : `${classes.salePrice} ${classes.selectedSalePrice}`}>
                            ${discountedProductPrice}
                        </span>
                        <span className={classes.discountAmount}>
                            ${round(sku.prices[0].value - sku.prices[1].value)} Off
                        </span>

                    </>
                )
                : (
                    <span className={selectedSku.id !== sku.id ? priceNonSelected : priceSelected}>
                        ${productPrice}
                    </span>

                );
        }
        return null;
    };
    // Starting at the end of the string (backwards pattern matching), continue to match any characters that aren't '-' or '_'
    const itemSku = (id) => id.match(/[^-_]*$/);

    const availabilityMessage = (sku) => {
        const deliveryMessage = sku?.availability?.deliveryMessage || product?.availability?.deliveryMessage || '';
        const earliestShipDate = new Date(sku?.availability?.earliestShipDate);
        const currentDate = new Date();
        if (earliestShipDate > currentDate) {
            return `NEXT AVAILABLE DATE: ${format(earliestShipDate, 'LLL do yyyy')}`;
        }
        if (deliveryMessage === 'AVAILABLE TO SHIP NOW') {
            return <div className={classes.availabletoShip}>{deliveryMessage}</div>;
        }
        return <div className={classes.productAvailibilityMessage}>{deliveryMessage}</div>;
    };

    let productName = selectedSku.name;
    if (isPassportBundleProduct?.enable) {
        if (productName?.length < 20) {
            if (isPassportBundleProduct?.skuText) {
                productName = `${productName} ${isPassportBundleProduct?.skuText}`;
            } else {
                productName += ' Bundle';
            }
        }
    }

    return (
        <>
            <div className={classes.productHeaderWrapper}>
                <div className={classes.titleAndPrice}>
                    <div className={classes.title}>{ReactHtmlParser(productName)}</div>
                    <div className={classes.price}>
                        {renderSkuPrice(selectedSku)}
                    </div>
                </div>
                <div className={classes.productId}>{itemSku(selectedSku?.id)}</div>
                <div className={classes.availability}>{availabilityMessage(selectedSku)}</div>
            </div>
        </>
    );
};

ProductNameRender.propTypes = {
    classes: object.isRequired,
    selectedSku: shape({
        id: string.isRequired,
        name: string.isRequired,
        prices: arrayOf(
            shape({
                type: string.isRequired,
                value: number.isRequired,
                __typename: string.isRequired,
            }),
        ).isRequired,
        __typename: string.isRequired,
    }).isRequired,
    product: shape({
        name: string.isRequired,
        partNumber: string.isRequired,
        shortDescription: string.isRequired,
        image: shape({
            name: string.isRequired,
            path: string.isRequired,
        }).isRequired,
        availability: shape({
            deliveryMessage: string.isRequired,
            productDeliveryType: string.isRequired,
        }).isRequired,
    }).isRequired,
    isPassportBundleProduct: shape({
        prices: {
            value: number,
        },
        enable: bool,
    }),
};

ProductNameRender.defaultProps = {
    isPassportBundleProduct: {},
};
const enhance = compose(
    withStyles(styles),
);

export default enhance(ProductNameRender);
